import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import Modal from './ui/Modal';
import { ICONS } from '../constants';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  detailMessage?: string;
  itemCount?: number;
  isDeleting?: boolean;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  detailMessage,
  itemCount = 1,
  isDeleting = false
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
    >
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
            <ICONS.AlertCircle className="w-10 h-10 text-red-500" />
          </div>
        </div>

        {/* Message */}
        <div className="text-center space-y-2">
          <p className="text-white text-lg">
            {message}
          </p>
          {itemCount > 1 && (
            <p className="text-gray-400 text-sm">
              {t('common.itemsSelected', { count: itemCount })}
            </p>
          )}
          {detailMessage && (
            <p className="text-gray-400 text-sm">
              {detailMessage}
            </p>
          )}
        </div>

        {/* Warning Note */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <ICONS.AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-200">
              {t('common.deleteWarning')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>{t('common.deleting')}</span>
              </>
            ) : (
              <>
                <ICONS.Trash className="w-4 h-4" />
                <span>{t('common.delete')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteConfirmModal;

